import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItem,
  LinearProgress,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import styled from "styled-components";
import ModalHeader from "./ModalHeader";
import {
  getActiveDownloads,
  subscribe,
} from "../../utils/download-manager";

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    background-color: #1F2735;
    color: white;
    min-width: 500px;
    max-width: 600px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 16px 24px;
  max-height: 60vh;
  overflow-y: auto;
  background-color: #1F2735;
  color: white;
`;

const DownloadItem = styled(ListItem)`
  background-color: ${({ status }) =>
    status === "downloading" || status === "pending"
      ? "#3b4252"
      : status === "completed"
      ? "#2e7d32"
      : status === "failed"
      ? "#b71c1c"
      : "#222"};
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const StatusChip = styled(Chip)`
  height: 24px;
  font-size: 12px;
`;

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
`;

const ActiveDownloadsModal = ({ open, onClose }) => {
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    if (!open) return;

    // Get initial downloads
    setDownloads(getActiveDownloads());
    
    console.log("ActiveDownloadsModal opened", getActiveDownloads());

    // Subscribe to updates
    const unsubscribe = subscribe((updatedDownloads) => {
      setDownloads(updatedDownloads);
    });

    return unsubscribe;
  }, [open]);


  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />;
      case "failed":
        return <ErrorIcon sx={{ color: "#f44336", fontSize: 20 }} />;
      case "downloading":
        return <DownloadIcon sx={{ color: "#2196f3", fontSize: 20 }} />;
      case "pending":
        return <DownloadIcon sx={{ color: "#9ca3af", fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "downloading":
        return "primary";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalHeader 
        title="Active Downloads" 
        onClose={onClose} 
        icon={<DownloadIcon sx={{ color: "#A5E0FF" }} />} 
      />
      <StyledDialogContent>
        {downloads.length === 0 ? (
          <EmptyState>
            <DownloadIcon sx={{ fontSize: 48, color: "#4b5563", mb: 2 }} />
            <Typography variant="body1" color="inherit">
              No active downloads
            </Typography>
          </EmptyState>
        ) : (
          <List>
            {downloads.map((download) => (
              <DownloadItem key={download.id} status={download.status}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                    {getStatusIcon(download.status)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#fbfeff",
                        }}
                      >
                        {download.filename || "Download"}
                      </Typography>
                      {download.description && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#A1A5AC",
                            fontSize: "0.75rem",
                            display: "block",
                            marginTop: "4px"
                          }}
                        >
                          Study: {download.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <StatusChip
                    label={download.status}
                    color={getStatusColor(download.status)}
                    size="small"
                  />
                </Box>
                {(download.status === "downloading" || download.status === "pending") && (
                  <>
                    {download.status === "downloading" && (
                      <LinearProgress
                        variant="determinate"
                        value={download.progress || 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    )}
                    {download.status === "pending" && (
                      <LinearProgress
                        variant="indeterminate"
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {download.status === "downloading" && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#A1A5AC",
                            fontSize: "0.75rem",
                            fontWeight: 500
                          }}
                        >
                          {download.progress?.toFixed(1) || 0}%
                        </Typography>
                      )}
                      {download.status === "pending" && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#A1A5AC",
                            fontSize: "0.75rem"
                          }}
                        >
                          Waiting in queue...
                        </Typography>
                      )}
                      {download.startTime && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "#A1A5AC",
                            fontSize: "0.75rem"
                          }}
                        >
                          {formatTime(download.startTime)}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
                {download.status === "completed" && download.endTime && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: "#A1A5AC",
                      fontSize: "0.75rem"
                    }}
                  >
                    Completed {formatTime(download.endTime)}
                  </Typography>
                )}
                {download.status === "failed" && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: "#f44336",
                      fontSize: "0.75rem"
                    }}
                  >
                    {download.error || "Download failed"}
                  </Typography>
                )}
              </DownloadItem>
            ))}
          </List>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default ActiveDownloadsModal;

